import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {find, union, without}                                                                      from 'lodash';
import {
  animate,
  state,
  style,
  transition,
  trigger
}                                                                                                  from '@angular/animations';
import {
  filter,
  map
}                                                                                                  from 'rxjs/operators';
import {
  connectJobWithFields,
  JobsStore
}                                                                                                  from '../../../stores/administration/jobs.store';
import {
  FieldStore
}                                                                                                  from '../../../stores/farm/field.store';
import {
  ApDynGridStatusColumnConfig
}                                                                                                  from '../../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import IFieldCropRotation = Data.FieldManagement.IFieldCropRotation;
import IGuid = System.IGuid;
import ICropTypes = Data.BaseData.ICropTypes;
import ICropRotationCell = Data.FieldManagement.ICropRotationCell;
import {
  ApKendoIconService
}                                                                                                  from '../../../icons/ap-kendo-icon.service';

/**
 * renders the Data for the CropRotation
 */
@Component({
  selector: 'ap-crop-rotation-list',
  templateUrl: './list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeInOutChildes', [
      state('in', style({})),
      state('out', style({height: '0'})),
      transition('in => out', animate(300)),
      transition('out => in', animate(300)),
    ]),
  ]
})
export class ListComponent implements OnChanges {

  constructor(private jobStore: JobsStore,
              private fieldStore: FieldStore) {
  }

  /**
   * the number of decimal places
   */
  @Input() public numericRound = 2;
  /**
   * the Loading Indicator
   */
  @Input() public loading: boolean;
  /**
   * a List of the Selected Field Ids
   */
  @Input() public selectedFields: IGuid[];
  /**
   * the sorted and filtered Items
   */
  @Input() public fields: IFieldCropRotation[];
  /**
   * the Years to display
   */
  @Input() public yearInterval: number[];
  /**
   * the current Campaign Year
   */
  @Input() public currentYear: number;
  /**
   * a List of CropTypes from the current Farm
   */
  @Input() public availableCropTypes: ICropTypes[] = [];
  /**
   * the Width of a single Cell
   */
  @Input() public columnWidth: number;
  /**
   * a List of Cells that currently was saving
   */
  @Input() public saving: ICropRotationCell[] = [];

  @Input() public wrapperHeight = 0;

  /**
   * fires when the Fields selection was changed
   */
  @Output() public selectedFieldsChange = new EventEmitter<IGuid[]>();
  /**
   * fires when a cell change its data
   */
  @Output() public saveCell = new EventEmitter<ICropRotationCell>();
  /**
   * fires when a Cell gets a Focus
   */
  @Output() public getFocus = new EventEmitter<[string, number]>();
  /**
   * fires when a Cell lost the Focus
   */
  @Output() public lostFocus = new EventEmitter<[string, number]>();

  /**
   * the animation State of the Children if visible or not
   */
  public visibleState: { [key: string]: string } = {};

  /**
   * the Value indicates if the Childes was rendered or not
   */
  public visibleValues: { [key: string]: boolean } = {};

  public fieldJobs$ = this.jobStore.FarmJobs$
    .pipe(
      filter(jobs => Array.isArray(jobs) && jobs.length > 0),
      map(jobs => connectJobWithFields(jobs, this.fieldStore))
    );

  public statusConfig = new ApDynGridStatusColumnConfig({
    field: 'Status',
    class: 'StatusClass',
    pending: this.jobStore.BlockedFieldIdsCe$,
    pendingTooltip: this.jobStore.BlockedFieldIdsCeTooltip$,
  });

  /**
   * track By Function for the fields ngFor
   * important for view re-rendering !!!!
   */
  public trackFieldList(index: number, item: IFieldCropRotation): string {
    return `${item.Id}-${item.FieldName}-${item.FieldNumber}-${item.FieldSubNumber}-${item.AreaHa}-` +
      `${item.Yields[0].MainCrop}-${item.Yields[0].MainCropYield}-${item.Yields[0].MainCropStraw}-${item.Yields[0].SecondCrop}-${item.Yields[0].SecondCropYield}` +
      `${item.Yields[1].MainCrop}-${item.Yields[1].MainCropYield}-${item.Yields[1].MainCropStraw}-${item.Yields[1].SecondCrop}-${item.Yields[1].SecondCropYield}` +
      `${item.Yields[2].MainCrop}-${item.Yields[2].MainCropYield}-${item.Yields[2].MainCropStraw}-${item.Yields[2].SecondCrop}-${item.Yields[2].SecondCropYield}` +
      `${item.Yields[3].MainCrop}-${item.Yields[3].MainCropYield}-${item.Yields[3].MainCropStraw}-${item.Yields[3].SecondCrop}-${item.Yields[3].SecondCropYield}` +
      `${item.Yields[4].MainCrop}-${item.Yields[4].MainCropYield}-${item.Yields[4].MainCropStraw}-${item.Yields[4].SecondCrop}-${item.Yields[4].SecondCropYield}` +
      `${item.Yields[5].MainCrop}-${item.Yields[5].MainCropYield}-${item.Yields[5].MainCropStraw}-${item.Yields[5].SecondCrop}-${item.Yields[5].SecondCropYield}` +
      `${item.Yields[6].MainCrop}-${item.Yields[6].MainCropYield}-${item.Yields[6].MainCropStraw}-${item.Yields[6].SecondCrop}-${item.Yields[6].SecondCropYield}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.fields) {
      this.fields = changes.fields.currentValue;
      this.visibleState = {};
      if (changes.fields.currentValue.length) {
        for (const c of changes.fields.currentValue as IFieldCropRotation[] | ICropRotationCell[]) {
          const id = (c as ICropRotationCell).FieldId ?
            (c as ICropRotationCell).FieldId :
            (c as IFieldCropRotation).Id;
          this.visibleState[id.toString()] = 'out';
          this.visibleValues[id.toString()] = false;
        }
      }
    }
  }

  /**
   * show/hide Child Rows
   */
  public switchVisibleState(fieldId: string): void {
    if (!this.visibleState.hasOwnProperty(fieldId)) {
      return;
    }
    this.visibleState[fieldId] = this.visibleState[fieldId] === 'in' ? 'out' : 'in';
    this.visibleValues[fieldId] = !this.visibleValues[fieldId];
  }

  /**
   * select a Field
   */
  public selectField(eventArgs: any, Id: IGuid): void {
    // ignore clicks on checkbox
    if (eventArgs.target.getAttribute('id')?.includes('grid_selector')) {
      return;
    }

    // join selection in case user holds CTRL key
    if (eventArgs.ctrlKey) {
      this.selectedFieldsChange.emit(union(this.selectedFields, [Id]));
      return;
    }

    // single select of a row if it is a regular click
    this.selectedFieldsChange.emit([Id]);
  }

  /**
   * select/unselect a Field Id
   */
  public toggleFieldSelection(Id: IGuid): void {
    if (this.findItem(Id)) {
      this.selectedFieldsChange.emit(without(this.selectedFields, Id));
    } else {
      this.selectedFieldsChange.emit(union(this.selectedFields, [Id]));
    }
  }

  /**
   * when a Child Animation begins
   */
  public onChildBeginAnimation(e, Id: IGuid): void {
    if (e.fromState === 'out' && e.toState === 'in') {
      this.visibleValues[Id.toString()] = true;
    }
  }

  /**
   * when a Child Animation is done
   */
  public onChildDoneAnimation(e, Id: IGuid): void {
    if (e.fromState === 'in' && e.toState === 'out') {
      this.visibleValues[Id.toString()] = false;
    }
  }

  /**
   * exists a Field with a Id
   */
  public findItem(Id: IGuid): boolean {
    return !!find(this.selectedFields, i => i === Id);
  }

  protected readonly ApKendoIconService = ApKendoIconService;
}
